import {
    IHixDefaultMappingsInfo,
    IHixMappingFieldValue,
    IHixTaskMapping,
    IHixTaskMappings,
    ISiteDesign,
    ISiteDesignField,
    ISiteDesignLibrary,
} from "../../data-structures/interfaces";
import {
    CUSTOM_TASK_PREFIX,
    DOCUMENT_TYPE_BY_ROW_KEY,
    HIX_CATEGORIES,
    KNOWN_CATEGORY_KEYS,
    STANDARD_TASK_TYPES,
    SUPPORTED_FIELD_TYPES,
    documentRowKey,
    isDocumentRowKey,
    isQuestionCategoryRowKey,
    isTemplateCategoryRowKey,
    mappingKey,
    questionCategoryKeyFromRow,
    questionCategoryRowKey,
    templateCategoryKeyFromRow,
    templateCategoryRowKey,
} from "./hixMappingKeys";

// Default HIX mappings (docs/superpowers/specs/2026-10-07-hix-default-mappings-design.md §3):
// pure helpers that turn the global defaults into the mappings panel's working state for one
// tenant, and merge them with what the tenant already has.

export type HixDefaultSkipReason =
    | "unknownKey"
    | "typeNotInDesign"
    | "typeNotLoaded"
    | "libraryNotInType"
    | "fieldNotInLibrary"
    | "fieldTypeNotAllowed"
    | "choiceValueNotAllowed"
    | "valueNotValidForType";

/** One part of the defaults that could not be applied to this tenant, and why. */
export interface IHixDefaultSkip {
    reason: HixDefaultSkipReason;
    /** The row's label (task, document or category), or the raw key when the key is unknown. */
    rowLabel: string;
    typeKey?: string;
    libraryTitle?: string;
    fieldName?: string;
    /** The rejected value (choiceValueNotAllowed and valueNotValidForType only). */
    value?: string;
}

/** The panel's working state: mappings keyed mappingKey(rowKey, typeKey), plus ignored row keys. */
export interface IHixMappingState {
    record: Record<string, IHixTaskMapping>;
    ignored: Set<string>;
}

export interface IHixDefaultsFilterResult extends IHixMappingState {
    skipped: IHixDefaultSkip[];
}

export interface IHixDefaultsOptions {
    /**
     * Lower-cased keys of the design types that came back without libraries (getUnloadedTypeKeys).
     * "Replace" leaves the tenant's entries on those types alone: the design failed to load them,
     * so the defaults cannot take their place and clearing them would only lose data.
     */
    unloadedTypeKeys?: ReadonlySet<string>;
}

interface IResolvedRow {
    rowKey: string;
    label: string;
    isTask: boolean;
}

const isText = (value: unknown): value is string => typeof value === "string";
const isKey = (value: unknown): value is string => typeof value === "string" && value.trim() !== "";
const isAbsent = (value: unknown) => value === undefined || value === null;
const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);
const listOf = <T>(value: T[] | undefined | null | unknown): T[] => (Array.isArray(value) ? value : []);

const sameText = (a: unknown, b: unknown) =>
    (isText(a) ? a : "").trim().toLowerCase() === (isText(b) ? b : "").trim().toLowerCase();

// The same word written with different spacing ("Inkomstenbelasting" / "Inkomsten belasting"),
// matched like Provisioning's delivery does (spec 2026-10-09 §1.2, deep audit).
const withoutSpaces = (value: string) => value.replace(/\s+/g, "").toLowerCase();

const isCustomKey = (key: string) => key.startsWith(CUSTOM_TASK_PREFIX);

const resolveTaskRow = (key: string): IResolvedRow | undefined => {
    const task = STANDARD_TASK_TYPES.find((t) => t.key === key);
    return task ? { rowKey: task.key, label: task.label, isTask: true } : undefined;
};

const resolveDocumentRow = (key: string): IResolvedRow | undefined => {
    const def = DOCUMENT_TYPE_BY_ROW_KEY.get(documentRowKey(key));
    return def ? { rowKey: documentRowKey(key), label: def.label, isTask: false } : undefined;
};

const resolveCategoryRow = (key: string, rowKey: (categoryKey: string) => string): IResolvedRow | undefined => {
    const category = HIX_CATEGORIES.find((c) => c.key === key);
    return category ? { rowKey: rowKey(key), label: category.label, isTask: false } : undefined;
};

/**
 * The site's choice for a stored value: the exact one (ignoring case and outer spaces) first,
 * else the one that differs only in spaces, as Provisioning's delivery matches it. Undefined for
 * a blank or unknown value.
 */
export const findChoice = (choices: readonly string[] | undefined, value: string): string | undefined => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return undefined;
    }
    const list = listOf<string>(choices);
    const wanted = withoutSpaces(trimmed);
    return list.find((c) => sameText(c, trimmed)) ?? list.find((c) => isText(c) && withoutSpaces(c) === wanted);
};

const NUMBER_FIELD_TYPES = ["Number", "Integer", "Currency"];
// What Provisioning's InvariantCulture int/double parse accepts (no thousands separators, no hex).
const NUMBER_PATTERN = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
// Provisioning's ISO formats: a date, optionally a 'T' or space and a time, then optionally Z or ±HH:mm.
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?(Z|([+-])(\d{2}):(\d{2}))?)?$/;
// The dates a SharePoint DateTime column can store (Provisioning's SharePointMinDate/MaxDate).
const SHAREPOINT_MIN_DATE = Date.UTC(1900, 0, 1);
const SHAREPOINT_MAX_DATE = Date.UTC(8900, 11, 31, 23, 59, 59);

/**
 * True when Provisioning can write the value to a DateTime column: one of its ISO formats, a real
 * calendar date and time, an offset within ±14:00, read as UTC when it has no offset, and an
 * instant between 1900-01-01 and 8900-12-31 23:59:59 UTC. Parsed by hand because Date.parse reads
 * an offset-less date-time as local time and is loose about seven-digit fractions.
 */
export const isHixIsoDate = (value: string): boolean => {
    const match = DATE_PATTERN.exec(value.trim());
    if (!match) {
        return false;
    }
    const [, y, mo, d, h = "0", mi = "0", s = "0", fraction = "", , sign, offH = "0", offM = "0"] = match;
    const [year, month, day, hour, minute, second] = [y, mo, d, h, mi, s].map(Number);
    const offset = (sign === "-" ? -1 : 1) * (Number(offH) * 60 + Number(offM));
    // Years 0–99 would be read as 1900–1999 by Date.UTC; a year this far out is out of range anyway.
    if (year < 1899 || year > 8901 || month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59
        || Number(offM) > 59 || Math.abs(offset) > 14 * 60) {
        return false;
    }
    const local = Date.UTC(year, month - 1, day, hour, minute, second);
    // Date.UTC rolls 2026-02-30 over to March; a different day means the date does not exist.
    if (new Date(local).getUTCDate() !== day) {
        return false;
    }
    const instant = local - offset * 60_000;
    const subSecond = /[1-9]/.test(fraction);
    return instant >= SHAREPOINT_MIN_DATE && (instant < SHAREPOINT_MAX_DATE || (instant === SHAREPOINT_MAX_DATE && !subSecond));
};

/**
 * The value as this column can take it, or undefined when it cannot. Empty stays empty for every
 * type, as the panel's own editors leave it. Boolean, number and date values are checked because
 * the panel's editors would hide a bad one (a Toggle shows "No", a number box shows nothing) while
 * it is still saved, and Provisioning then skips or rejects it.
 */
const valueForColumn = (column: ISiteDesignField, raw: string): string | undefined => {
    const trimmed = raw.trim();
    if (column.fieldType === "Choice") {
        return trimmed === "" ? "" : findChoice(column.choices, trimmed);
    }
    if (column.fieldType === "Boolean") {
        return trimmed === "" ? "" : ["true", "false"].includes(trimmed.toLowerCase()) ? trimmed.toLowerCase() : undefined;
    }
    if (NUMBER_FIELD_TYPES.includes(column.fieldType)) {
        return trimmed === "" || NUMBER_PATTERN.test(trimmed) ? trimmed : undefined;
    }
    if (column.fieldType === "DateTime") {
        return trimmed === "" || isHixIsoDate(trimmed) ? trimmed : undefined;
    }
    return raw;
};

/**
 * Keeps only the parts of the defaults this tenant's site design can map (spec §3.2). Types,
 * libraries, columns and choices are matched ignoring case and rewritten to the design's exact
 * names, so every kept entry lands on a rendered row and type section.
 */
export const filterDefaultsForDesign = (defaults: IHixTaskMappings, design: ISiteDesign): IHixDefaultsFilterResult => {
    const record: Record<string, IHixTaskMapping> = {};
    const ignored = new Set<string>();
    const skipped: IHixDefaultSkip[] = [];
    const types = listOf<ISiteDesign["types"][number]>(design?.types).filter((t) => isKey(t?.key));

    const addEntry = (row: IResolvedRow, typeKey: unknown, libraryTitle: unknown, fields: unknown) => {
        if (!isKey(libraryTitle)) {
            return; // No library means "unmapped": not a mapping at all.
        }
        const type = types.find((t) => sameText(t.key, typeKey));
        if (!type) {
            skipped.push({ reason: "typeNotInDesign", rowLabel: row.label, typeKey: isText(typeKey) ? typeKey : String(typeKey) });
            return;
        }
        const key = mappingKey(row.rowKey, type.key);
        if (record[key]) {
            return; // A second entry for the same row and type: the first wins, as in Provisioning's router.
        }
        const libraries = listOf<ISiteDesignLibrary>(type.libraries);
        if (!libraries.length) {
            // The type exists but came back without libraries (e.g. a failed capture), so nothing
            // can be checked against it — not the same as the library being missing.
            skipped.push({ reason: "typeNotLoaded", rowLabel: row.label, typeKey: type.key });
            return;
        }
        const library = libraries.find((l) => sameText(l?.title, libraryTitle));
        if (!library) {
            skipped.push({ reason: "libraryNotInType", rowLabel: row.label, typeKey: type.key, libraryTitle });
            return;
        }

        const kept: IHixMappingFieldValue[] = [];
        const keptNames = new Set<string>();
        listOf<unknown>(fields).forEach((field) => {
            const name = isObject(field) && isText(field.internalName) ? field.internalName.trim() : "";
            if (!name || keptNames.has(name.toLowerCase())) {
                return;
            }
            const where = { rowLabel: row.label, typeKey: type.key, libraryTitle: library.title, fieldName: name };
            const column = listOf<ISiteDesignField>(library.fields).find((c) => sameText(c?.internalName, name));
            if (!column) {
                skipped.push({ reason: "fieldNotInLibrary", ...where });
                return;
            }
            if (!SUPPORTED_FIELD_TYPES.includes(column.fieldType)) {
                skipped.push({ reason: "fieldTypeNotAllowed", ...where });
                return;
            }
            const raw = isObject(field) && isText(field.value) ? field.value : "";
            const value = valueForColumn(column, raw);
            if (value === undefined) {
                skipped.push({
                    reason: column.fieldType === "Choice" ? "choiceValueNotAllowed" : "valueNotValidForType",
                    ...where,
                    value: raw,
                });
                return;
            }
            keptNames.add(name.toLowerCase());
            kept.push({ internalName: column.internalName, value });
        });

        record[key] = {
            taskTypeKey: row.rowKey,
            ...(row.isTask ? { taskTypeLabel: row.label } : {}),
            isCustom: false,
            typeKey: type.key,
            libraryTitle: library.title,
            fields: kept,
        };
    };

    const addIgnored = (keys: unknown, resolve: (key: string) => IResolvedRow | undefined) => {
        listOf<unknown>(keys).forEach((key) => {
            if (!isKey(key) || isCustomKey(key)) {
                return;
            }
            const row = resolve(key);
            if (!row) {
                skipped.push({ reason: "unknownKey", rowLabel: key });
                return;
            }
            ignored.add(row.rowKey);
        });
    };

    listOf<IHixTaskMapping>(defaults?.mappings).forEach((m) => {
        const key = m?.taskTypeKey;
        if (!isKey(key) || m.isCustom === true || isCustomKey(key)) {
            return; // Custom task types are per tenant and never part of the defaults.
        }
        const row = resolveTaskRow(key);
        if (!row) {
            skipped.push({ reason: "unknownKey", rowLabel: key });
            return;
        }
        addEntry(row, m.typeKey, m.libraryTitle, m.fields);
    });

    listOf<NonNullable<IHixTaskMappings["documentMappings"]>[number]>(defaults?.documentMappings).forEach((m) => {
        if (!isKey(m?.documentTypeKey)) {
            return;
        }
        const row = resolveDocumentRow(m.documentTypeKey);
        if (!row) {
            skipped.push({ reason: "unknownKey", rowLabel: m.documentTypeKey });
            return;
        }
        addEntry(row, m.typeKey, m.libraryTitle, m.fields);
    });

    const addCategoryEntries = (
        entries: IHixTaskMappings["questionCategoryMappings"],
        rowKey: (categoryKey: string) => string
    ) => {
        listOf<NonNullable<IHixTaskMappings["questionCategoryMappings"]>[number]>(entries).forEach((m) => {
            if (!isKey(m?.categoryKey)) {
                return;
            }
            const row = resolveCategoryRow(m.categoryKey, rowKey);
            if (!row) {
                skipped.push({ reason: "unknownKey", rowLabel: m.categoryKey });
                return;
            }
            addEntry(row, m.typeKey, m.libraryTitle, m.fields);
        });
    };

    addCategoryEntries(defaults?.questionCategoryMappings, questionCategoryRowKey);
    addCategoryEntries(defaults?.templateCategoryMappings, templateCategoryRowKey);

    addIgnored(defaults?.ignoredTaskTypes, resolveTaskRow);
    addIgnored(defaults?.ignoredDocumentTypes, resolveDocumentRow);
    addIgnored(defaults?.ignoredQuestionCategories, (k) => resolveCategoryRow(k, questionCategoryRowKey));
    addIgnored(defaults?.ignoredTemplateCategories, (k) => resolveCategoryRow(k, templateCategoryRowKey));

    return { record, ignored, skipped };
};

export type HixDefaultsMergeMode = "fill" | "replace";

export interface IHixDefaultsMergeResult {
    state: IHixMappingState;
    mappingsAdded: number;
    ignoresAdded: number;
}

// Rows the defaults speak for: the standard task types, document types and categories this build
// knows. Custom task types and rows saved by a newer build or Provisioning (which the panel may
// not even show) are never touched.
const isCoveredRow = (rowKey: string): boolean => {
    if (!isText(rowKey)) {
        return false;
    }
    if (isDocumentRowKey(rowKey)) {
        return DOCUMENT_TYPE_BY_ROW_KEY.has(rowKey);
    }
    if (isQuestionCategoryRowKey(rowKey)) {
        return KNOWN_CATEGORY_KEYS.has(questionCategoryKeyFromRow(rowKey));
    }
    if (isTemplateCategoryRowKey(rowKey)) {
        return KNOWN_CATEGORY_KEYS.has(templateCategoryKeyFromRow(rowKey));
    }
    return STANDARD_TASK_TYPES.some((t) => t.key === rowKey);
};

// A tenant entry "replace" may remove: on a covered row, and not on a type the design failed to
// load. Entries on types the design no longer has are covered — the panel shows them read-only,
// so Replace is the only way to clear them.
const isCoveredEntry = (m: IHixTaskMapping, unloadedTypeKeys?: ReadonlySet<string>) =>
    isCoveredRow(m?.taskTypeKey) &&
    !(unloadedTypeKeys?.has((isText(m.typeKey) ? m.typeKey : "").toLowerCase()) ?? false);

const cloneMapping = (m: IHixTaskMapping): IHixTaskMapping => ({ ...m, fields: (m.fields ?? []).map((f) => ({ ...f })) });

/** Lower-cased keys of the design types that came back without libraries (they failed to load). */
export const getUnloadedTypeKeys = (design: ISiteDesign | undefined): Set<string> =>
    new Set(
        listOf<ISiteDesign["types"][number]>(design?.types)
            .filter((t) => isKey(t?.key) && listOf(t.libraries).length === 0)
            .map((t) => t.key.toLowerCase())
    );

/** True when "Replace with defaults" would remove something the tenant has (spec §4.2). */
export const hasCoveredContent = (current: IHixMappingState, options: IHixDefaultsOptions = {}): boolean =>
    Object.values(current.record).some((m) => !!m?.libraryTitle && isCoveredEntry(m, options.unloadedTypeKeys)) ||
    Array.from(current.ignored).some((key) => isCoveredRow(key));

/**
 * Applies filtered defaults to the panel's working state (spec §3.3). "fill" only adds what the
 * tenant does not have; "replace" swaps every covered row for the defaults. Inputs are never
 * mutated; default entries are copied so later edits cannot reach the defaults.
 */
export const mergeDefaults = (
    current: IHixMappingState,
    defaults: IHixMappingState,
    mode: HixDefaultsMergeMode,
    options: IHixDefaultsOptions = {}
): IHixDefaultsMergeResult => {
    let mappingsAdded = 0;
    let ignoresAdded = 0;

    if (mode === "replace") {
        const record: Record<string, IHixTaskMapping> = {};
        Object.entries(current.record).forEach(([key, m]) => {
            if (!isCoveredEntry(m, options.unloadedTypeKeys)) {
                record[key] = m;
            }
        });
        const ignored = new Set(Array.from(current.ignored).filter((key) => !isCoveredRow(key)));
        Object.entries(defaults.record).forEach(([key, m]) => {
            if (isCoveredRow(m.taskTypeKey)) {
                record[key] = cloneMapping(m);
                mappingsAdded++;
            }
        });
        defaults.ignored.forEach((rowKey) => {
            if (isCoveredRow(rowKey)) {
                ignored.add(rowKey);
                ignoresAdded++;
            }
        });
        return { state: { record, ignored }, mappingsAdded, ignoresAdded };
    }

    const record = { ...current.record };
    const ignored = new Set(current.ignored);
    const mappedRows = new Set(Object.values(current.record).filter((m) => m?.libraryTitle).map((m) => m.taskTypeKey));
    Object.entries(defaults.record).forEach(([key, m]) => {
        if (!isCoveredRow(m.taskTypeKey) || record[key]?.libraryTitle) {
            return;
        }
        record[key] = cloneMapping(m);
        mappingsAdded++;
    });
    defaults.ignored.forEach((rowKey) => {
        if (!isCoveredRow(rowKey) || ignored.has(rowKey) || mappedRows.has(rowKey)) {
            return;
        }
        ignored.add(rowKey);
        ignoresAdded++;
    });
    return { state: { record, ignored }, mappingsAdded, ignoresAdded };
};

/** The payload "Save as defaults" sends: custom task types are per tenant and never defaults. */
export const stripCustomTaskTypes = (payload: IHixTaskMappings): IHixTaskMappings => ({
    ...payload,
    mappings: (payload.mappings ?? []).filter((m) => !m.isCustom && !isCustomKey(m.taskTypeKey ?? "")),
    ignoredTaskTypes: (payload.ignoredTaskTypes ?? []).filter((key) => !isCustomKey(key)),
});

/** True when the payload has at least one mapping or ignore. The API rejects an empty defaults set. */
export const hasDefaultsContent = (payload: IHixTaskMappings): boolean =>
    [
        payload.mappings,
        payload.ignoredTaskTypes,
        payload.documentMappings,
        payload.ignoredDocumentTypes,
        payload.templateCategoryMappings,
        payload.ignoredTemplateCategories,
        payload.questionCategoryMappings,
        payload.ignoredQuestionCategories,
    ].some((section) => (section?.length ?? 0) > 0);

/**
 * The API's size budget for a mappings blob (HixMappingsJsonValidation.MaxEscapedUtf8Bytes; keep
 * the two equal): the blob as ResolveTenant sends it to Provisioning, escaped and UTF-8 encoded.
 */
export const HIX_MAPPINGS_MAX_ESCAPED_BYTES = 56 * 1024;

/**
 * The size of a JSON string as the API measures it: escaped as a JSON string the way Newtonsoft
 * does (quotes and backslashes; control characters; U+0085, U+2028 and U+2029 as \uXXXX), then
 * UTF-8 encoded, with an unpaired surrogate counted as U+FFFD.
 */
export const escapedUtf8ByteCount = (json: string): number => {
    let bytes = 2; // the enclosing quotes
    for (let i = 0; i < json.length; i++) {
        const code = json.charCodeAt(i);
        if (code === 0x22 || code === 0x5c) {
            bytes += 2;
        } else if (code < 0x20) {
            bytes += [0x08, 0x09, 0x0a, 0x0c, 0x0d].includes(code) ? 2 : 6;
        } else if (code === 0x85 || code === 0x2028 || code === 0x2029) {
            bytes += 6;
        } else if (code < 0x80) {
            bytes += 1;
        } else if (code < 0x800) {
            bytes += 2;
        } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < json.length && json.charCodeAt(i + 1) >= 0xdc00 && json.charCodeAt(i + 1) <= 0xdfff) {
            bytes += 4;
            i++;
        } else {
            bytes += 3;
        }
    }
    return bytes;
};

/** True when the payload, as the API helpers send it (JSON.stringify), is over the API's budget. */
export const exceedsMappingsSizeLimit = (payload: IHixTaskMappings): boolean =>
    escapedUtf8ByteCount(JSON.stringify(payload)) > HIX_MAPPINGS_MAX_ESCAPED_BYTES;

export interface IHixMappingsRead {
    mappings: IHixTaskMappings;
    /** How many sections, entries, fields or ignore keys were left out because they were malformed. */
    unreadable: number;
}

/**
 * Reads a stored mappings blob defensively, so nothing malformed can reach the panel's state or
 * rendering: sections that are not lists, entries that are not objects or carry members of the
 * wrong type, malformed fields and ignore keys are left out and counted. A missing field value
 * reads as empty text. Undefined when it is not a version-1 object. Strict (the defaults) also
 * requires a type key, a library and named fields, as the API does; lenient (a tenant's own blob)
 * keeps the panel's existing tolerance for entries without a type key, which it drops on load.
 */
export const readHixTaskMappings = (raw: unknown, options: { strict?: boolean } = {}): IHixMappingsRead | undefined => {
    if (!isObject(raw) || raw.version !== 1) {
        return undefined;
    }
    const strict = options.strict === true;
    let unreadable = 0;
    const result: Record<string, unknown> = { ...raw };

    const readList = (name: string, readItem: (item: unknown) => unknown) => {
        const value = raw[name];
        if (isAbsent(value)) {
            return;
        }
        if (!Array.isArray(value)) {
            delete result[name];
            unreadable++;
            return;
        }
        const items: unknown[] = [];
        value.forEach((item) => {
            const read = readItem(item);
            if (read === undefined) {
                unreadable++;
            } else {
                items.push(read);
            }
        });
        result[name] = items;
    };

    const readFields = (entry: Record<string, unknown>) => {
        if (isAbsent(entry.fields)) {
            return entry;
        }
        if (!Array.isArray(entry.fields)) {
            return undefined;
        }
        const fields: IHixMappingFieldValue[] = [];
        entry.fields.forEach((field) => {
            const named = isObject(field) && (strict ? isKey(field.internalName) : isText(field.internalName));
            const valued = isObject(field) && (isText(field.value) || (!strict && isAbsent(field.value)));
            if (named && valued) {
                fields.push({ ...field, internalName: field.internalName as string, value: isText(field.value) ? field.value : "" });
            } else {
                unreadable++;
            }
        });
        return { ...entry, fields };
    };

    const readEntry = (keyName: string) => (item: unknown) => {
        if (!isObject(item) || !isKey(item[keyName])) {
            return undefined;
        }
        const optionalText = (value: unknown) => isAbsent(value) || isText(value);
        if (!optionalText(item.typeKey) || !optionalText(item.libraryTitle) || !optionalText(item.taskTypeLabel)) {
            return undefined;
        }
        if (!(isAbsent(item.isCustom) || typeof item.isCustom === "boolean")) {
            return undefined;
        }
        if (!(isAbsent(item.hixUploadTypeId) || typeof item.hixUploadTypeId === "number")) {
            return undefined;
        }
        if (strict && (!isKey(item.typeKey) || !isKey(item.libraryTitle))) {
            return undefined;
        }
        return readFields(item);
    };

    readList("mappings", readEntry("taskTypeKey"));
    readList("documentMappings", readEntry("documentTypeKey"));
    readList("templateCategoryMappings", readEntry("categoryKey"));
    readList("questionCategoryMappings", readEntry("categoryKey"));
    ["ignoredTaskTypes", "ignoredDocumentTypes", "ignoredTemplateCategories", "ignoredQuestionCategories"].forEach((name) =>
        readList(name, (item) => (isKey(item) ? item : undefined))
    );

    return { mappings: result as unknown as IHixTaskMappings, unreadable };
};

/**
 * Rewrites saved type keys to the design's spelling when they differ only in case or spacing, so
 * those entries show in their type section and survive Save (the panel looks sections up by exact
 * key). When spellings collide on one row, the exact one wins, otherwise the first. Types the
 * design does not have are left as they are.
 */
export const alignTypeKeys = <T extends { typeKey?: string }>(
    entries: T[],
    rowKeyOf: (entry: T) => string,
    design: ISiteDesign | undefined
): T[] => {
    const designKeys = listOf<ISiteDesign["types"][number]>(design?.types).map((t) => t?.key).filter(isKey);
    if (!designKeys.length) {
        return entries;
    }
    const exact = new Set(
        entries.filter((e) => isText(e.typeKey) && designKeys.includes(e.typeKey)).map((e) => mappingKey(rowKeyOf(e), e.typeKey as string))
    );
    const taken = new Set<string>();
    const aligned: T[] = [];
    entries.forEach((entry) => {
        const designKey = designKeys.find((k) => sameText(k, entry.typeKey));
        if (!designKey || designKey === entry.typeKey) {
            aligned.push(entry);
            return;
        }
        const key = mappingKey(rowKeyOf(entry), designKey);
        if (exact.has(key) || taken.has(key)) {
            return;
        }
        taken.add(key);
        aligned.push({ ...entry, typeKey: designKey });
    });
    return aligned;
};

/**
 * Reads the GET api/portal/HixDefaultMappings body. The API omits null members, so absent means
 * null. Returns undefined when the stored value is not a readable version-1 object, or when any
 * part of it is malformed, which the panel treats like a failed load.
 */
export const parseHixDefaultMappingsResponse = (data: any): IHixDefaultMappingsInfo | undefined => {
    const configurationKey = typeof data?.configurationKey === "string" ? data.configurationKey : null;
    const updatedAtUtc = typeof data?.updatedAtUtc === "string" ? data.updatedAtUtc : null;
    const updatedBy = typeof data?.updatedBy === "string" ? data.updatedBy : null;
    const raw = data?.hixDefaultMappings;
    if (!raw) {
        return { configurationKey, mappings: null, updatedAtUtc, updatedBy };
    }
    try {
        const read = readHixTaskMappings(JSON.parse(raw), { strict: true });
        return read && read.unreadable === 0 ? { configurationKey, mappings: read.mappings, updatedAtUtc, updatedBy } : undefined;
    } catch {
        return undefined;
    }
};
